import { datadogLogs } from '@datadog/browser-logs';
import { Resources } from '@strength/i18n';
import { ReactNode } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import RemoteAppContainerError from './RemoteAppContainerError';

// TKey (keyof the full translation resource) includes a few nested-object
// keys (e.g. "blockSummary") that t()'s single-argument overload rejects.
// LeafKey excludes those so titleKey/bodyKey can use the plain t(key) call
// the same way every hand-written boundary before this factory did.
type Translation = Resources['translation'];
type LeafKey = {
  [K in keyof Translation]: Translation[K] extends string ? K : never;
}[keyof Translation];

interface ErrorBoundaryConfig {
  /** Tagged on the Datadog log as `boundary` and used as the component's displayName. */
  name: string;
  logMessage: string;
  titleKey: LeafKey;
  bodyKey: LeafKey;
  secondaryAction?: ReactNode;
  /** Extra fields merged into the Datadog log context, e.g. `{ page: 'reporting' }`. */
  logContext?: Record<string, unknown>;
}

type Props = { children: ReactNode };

export function createErrorBoundary({
  name,
  logMessage,
  titleKey,
  bodyKey,
  secondaryAction,
  logContext,
}: ErrorBoundaryConfig) {
  const Fallback = (_: FallbackProps) => {
    const { t } = useTranslation();
    return (
      <RemoteAppContainerError
        refresh={() => window.location.reload()}
        failureHeader={t(titleKey)}
        failureDescription={t(bodyKey)}
        secondaryAction={secondaryAction}
      />
    );
  };

  const NamedErrorBoundary = ({ children }: Props) => (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={(error, info) => {
        // React allows throwing non-Error values (e.g. `throw 'oops'`), but
        // react-error-boundary's onError types it as `unknown`. Datadog's
        // dedicated error argument requires a real Error, so coerce here —
        // this also means Datadog can extract a stack trace either way.
        // Keep the original thrown value in context when it wasn't already
        // an Error, so a non-Error throw (e.g. a plain object with useful
        // fields) doesn't lose that data to String() coercion.
        datadogLogs.logger.error(
          logMessage,
          {
            ...logContext,
            ...(error instanceof Error ? {} : { originalThrownValue: error }),
            componentStack: info.componentStack,
            boundary: name,
          },
          error instanceof Error ? error : new Error(String(error)),
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
  NamedErrorBoundary.displayName = name;

  return NamedErrorBoundary;
}
